// ============================================================
// Pathfinding.js - 2D A* grid pathfinding for hostile mobs
// Works on the XZ plane, checks Y for walkability
// ============================================================

/**
 * Simple binary min-heap for A* open set.
 */
class MinHeap {
  constructor() { this._data = []; }

  push(node) {
    this._data.push(node);
    this._bubbleUp(this._data.length - 1);
  }

  pop() {
    const top = this._data[0];
    const last = this._data.pop();
    if (this._data.length > 0) {
      this._data[0] = last;
      this._sinkDown(0);
    }
    return top;
  }

  get size() { return this._data.length; }

  _bubbleUp(i) {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this._data[i].f < this._data[parent].f) {
        [this._data[i], this._data[parent]] = [this._data[parent], this._data[i]];
        i = parent;
      } else break;
    }
  }

  _sinkDown(i) {
    const n = this._data.length;
    while (true) {
      let smallest = i;
      const left = 2 * i + 1;
      const right = 2 * i + 2;
      if (left < n && this._data[left].f < this._data[smallest].f) smallest = left;
      if (right < n && this._data[right].f < this._data[smallest].f) smallest = right;
      if (smallest !== i) {
        [this._data[i], this._data[smallest]] = [this._data[smallest], this._data[i]];
        i = smallest;
      } else break;
    }
  }
}

// 4-directional neighbors (no diagonal — simpler, more Minecraft-like)
const DIRS = [
  { dx: 1, dz: 0 },
  { dx: -1, dz: 0 },
  { dx: 0, dz: 1 },
  { dx: 0, dz: -1 },
];

/**
 * Find a path from start to goal on the XZ plane.
 *
 * @param {Object} world - Must have getBlock(x, y, z)
 * @param {{ x: number, y: number, z: number }} start - Start position (feet)
 * @param {{ x: number, y: number, z: number }} goal - Target position (feet)
 * @param {number} [maxSteps=200] - Max nodes to explore (perf budget)
 * @returns {Array<{ x: number, y: number, z: number }>|null} Path as array of positions, or null
 */
export function findPath(world, start, goal, maxSteps = 200) {
  const sx = Math.floor(start.x);
  const sy = Math.floor(start.y);
  const sz = Math.floor(start.z);
  const gx = Math.floor(goal.x);
  const gz = Math.floor(goal.z);

  // Already at goal?
  if (sx === gx && sz === gz) return [];

  const open = new MinHeap();
  const closed = new Set();
  const gScores = new Map();

  const startKey = `${sx},${sz}`;
  const h0 = Math.abs(gx - sx) + Math.abs(gz - sz); // Manhattan distance
  open.push({ x: sx, y: sy, z: sz, g: 0, f: h0, parent: null, key: startKey });
  gScores.set(startKey, 0);

  let steps = 0;

  while (open.size > 0 && steps < maxSteps) {
    steps++;
    const current = open.pop();

    // Goal reached?
    if (current.x === gx && current.z === gz) {
      return _reconstructPath(current);
    }

    closed.add(current.key);

    for (const dir of DIRS) {
      const nx = current.x + dir.dx;
      const nz = current.z + dir.dz;
      const nKey = `${nx},${nz}`;

      if (closed.has(nKey)) continue;

      // Find the Y level for this XZ position (walk up/down 1 block)
      const ny = _findWalkableY(world, nx, current.y, nz);
      if (ny === null) continue; // not walkable

      // Cost: 1 per step + extra for vertical change
      const stepCost = 1 + (ny !== current.y ? 0.5 : 0);
      const tentG = current.g + stepCost;

      const prevG = gScores.get(nKey);
      if (prevG !== undefined && tentG >= prevG) continue;

      gScores.set(nKey, tentG);
      const h = Math.abs(gx - nx) + Math.abs(gz - nz);
      open.push({
        x: nx,
        y: ny,
        z: nz,
        g: tentG,
        f: tentG + h,
        parent: current,
        key: nKey,
      });
    }
  }

  // No path found within budget
  return null;
}

/**
 * Find a walkable Y level at (x, z) near the current Y.
 * Mobs can step up 1 block or drop down 3 blocks.
 * Returns the Y of the foot position, or null if unwalkable.
 */
function _findWalkableY(world, x, currentY, z) {
  // Check same level first
  if (_isWalkable(world, x, currentY, z)) return currentY;

  // Step up 1 block
  if (_isWalkable(world, x, currentY + 1, z)) return currentY + 1;

  // Step down 1-3 blocks
  for (let dy = 1; dy <= 3; dy++) {
    if (_isWalkable(world, x, currentY - dy, z)) return currentY - dy;
  }

  return null;
}

/**
 * Check if a mob can stand at (x, y, z).
 * Requires: solid block at y-1, air at y and y+1 (2 blocks tall).
 */
function _isWalkable(world, x, y, z) {
  const below = world.getBlock(x, y - 1, z);
  const feet = world.getBlock(x, y, z);
  const head = world.getBlock(x, y + 1, z);

  // Below must be solid (non-zero, non-passable)
  if (below === 0 || below === undefined) return false;
  // Check passability of below block (water, air, etc are passable = not solid)
  if (_isPassableBlock(below)) return false;

  // Feet and head must be passable (air, water, etc)
  if (feet !== 0 && !_isPassableBlock(feet)) return false;
  if (head !== 0 && !_isPassableBlock(head)) return false;

  return true;
}

/**
 * Check if a block ID is passable (can be walked through).
 * Simple check: 0 = air, water/grass/flowers are passable.
 */
function _isPassableBlock(blockId) {
  // 0 = AIR (always passable)
  if (blockId === 0) return true;
  // Common passable blocks: water, tall grass, flowers, wheat, etc.
  // BlockId values from BlockType.js
  const passable = [8, 9, 50, 51, 52, 53, 54, 55, 56, 57, 58, 70]; // water variants, vegetation
  return passable.includes(blockId);
}

/**
 * Reconstruct path from goal node back to start.
 */
function _reconstructPath(node) {
  const path = [];
  let current = node;
  while (current.parent) {
    path.unshift({ x: current.x + 0.5, y: current.y, z: current.z + 0.5 });
    current = current.parent;
  }
  return path;
}

/**
 * Get the next direction to move toward the target, using A* pathfinding.
 * Returns a normalized XZ direction vector, or null if no path.
 *
 * @param {Object} world
 * @param {{ x: number, y: number, z: number }} mobPos
 * @param {{ x: number, y: number, z: number }} targetPos
 * @param {number} [maxSteps=100]
 * @returns {{ x: number, z: number }|null} Normalized direction, or null
 */
export function getPathDirection(world, mobPos, targetPos, maxSteps = 100) {
  const path = findPath(world, mobPos, targetPos, maxSteps);

  if (!path || path.length === 0) {
    // Fallback: direct line (original behavior)
    const dx = targetPos.x - mobPos.x;
    const dz = targetPos.z - mobPos.z;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len < 0.01) return null;
    return { x: dx / len, z: dz / len };
  }

  // Move toward the first waypoint
  const next = path[0];
  const dx = next.x - mobPos.x;
  const dz = next.z - mobPos.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 0.01) return path.length > 1 ? { x: path[1].x - mobPos.x, z: path[1].z - mobPos.z } : null;
  return { x: dx / len, z: dz / len };
}
